/**
 * Amazon Ads — DSP TECHNICAL Column Map (Story 19.16.1, Task 5)
 *
 * 5 stable-schema metrics + 3 enum-extensible dimensions for the newer 2022+
 * TECHNICAL/TECH report-type. Unlike AUDIENCE + AUDIO_VIDEO, the TECHNICAL
 * schema is documented and stable — no runtime introspection.
 *
 * Enum extensibility (Round 2 Pre-mortem D carry-forward): unknown enum values
 * surface as WARNINGS via `amazon_ads_dsp_technical_unknown_enum_value` log —
 * NOT as malformed records. Records pass through with the unknown value
 * preserved verbatim. This prevents Amazon enum extensions (e.g.
 * `"VR_HEADSET"`) from breaking the entire fetch.
 */

import type { DspMetricColumnEntry } from "./dsp-column-map";

const KPI_LINE_BAR_TABLE = ["kpi", "line-chart", "bar-chart", "data-table"] as const;
const KPI_LINE_TABLE = ["kpi", "line-chart", "data-table"] as const;

/**
 * Story 19.16.1 AC #9 — TECHNICAL metric column map. 5 metrics covering the
 * device/environment/measurement-source breakdown.
 */
export const DSP_TECHNICAL_COLUMN_MAP: Record<string, DspMetricColumnEntry> = {
  technicalImpressions: {
    key: "technicalImpressions",
    label: "Technical Impressions",
    reportTypeId: "DSP_TECHNICAL",
    columnName: "technicalImpressions",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  technicalClicks: {
    key: "technicalClicks",
    label: "Technical Clicks",
    reportTypeId: "DSP_TECHNICAL",
    columnName: "technicalClicks",
    dataType: "Integer",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  technicalCost: {
    key: "technicalCost",
    label: "Technical Cost",
    reportTypeId: "DSP_TECHNICAL",
    columnName: "technicalCost",
    dataType: "Currency",
    aggregationKind: "additive",
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
  technicalPurchases14d: {
    key: "technicalPurchases14d",
    label: "Technical Purchases (14d)",
    reportTypeId: "DSP_TECHNICAL",
    columnName: "technicalPurchases14d",
    dataType: "Integer",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_BAR_TABLE],
  },
  technicalSales14d: {
    key: "technicalSales14d",
    label: "Technical Sales (14d)",
    reportTypeId: "DSP_TECHNICAL",
    columnName: "technicalSales14d",
    dataType: "Currency",
    aggregationKind: "additive",
    attributionWindowDays: 14,
    compatibleWidgets: [...KPI_LINE_TABLE],
  },
};

/**
 * Story 19.16.1 AC #9 — known-enum-values constants. Per-field arrays so
 * `validateTechnicalEnumValue` can switch by field name.
 *
 * **Frozen `as const`** — never hot-loaded. New enum values fall through to
 * the warning-with-pass-through path so Amazon-side extensions don't break
 * the fetch.
 */
export const TECHNICAL_KNOWN_ENUM_VALUES = {
  device: ["DESKTOP", "TABLET", "PHONE", "TV", "GAMING_CONSOLE", "OTHER"],
  environment: ["WEB", "MOBILE_APP", "CTV", "FIRE_TABLET", "FIRE_TV"],
  measurementSource: [
    "AMAZON_FIRST_PARTY",
    "DOUBLECLICK",
    "ADOBE_ANALYTICS",
    "OTHER_3P",
  ],
} as const;

export type TechnicalEnumField = keyof typeof TECHNICAL_KNOWN_ENUM_VALUES;

/**
 * Story 19.16.1 AC #9 / Task 5.4 — enum-extensibility tolerance. Unknown enum
 * values surface as warnings (not malformed records) so Amazon can add
 * `"VR_HEADSET"` etc. without breaking the fetch.
 */
export function validateTechnicalEnumValue(
  field: TechnicalEnumField,
  value: string
): { valid: true; isKnownValue: boolean } {
  const knownValues = TECHNICAL_KNOWN_ENUM_VALUES[field];
  if ((knownValues as readonly string[]).includes(value)) {
    return { valid: true, isKnownValue: true };
  }
  console.log(
    JSON.stringify({
      event: "amazon_ads_dsp_technical_unknown_enum_value",
      field,
      value,
      timestamp: new Date().toISOString(),
    })
  );
  return { valid: true, isKnownValue: false };
}

/**
 * Defensive constant — the same 31-day chunk ceiling as DSP_CAMPAIGN. Amazon
 * does NOT publish a separate max for TECHNICAL; this matches the family.
 */
export const AMAZON_ADS_DSP_TECHNICAL_MAX_DAYS_PER_REPORT = 31;
